import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCardIcon, DollarSignIcon, CalendarIcon, BellIcon, TrendingUpIcon, ArrowUpRightIcon, BarChart3Icon, PieChartIcon, PlusIcon } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { subscriptions as initialSubscriptions, Subscription } from '../utils/mockData';
import SubscriptionForm from '../components/SubscriptionForm';
import Calendar from '../components/Calendar';
const Home = () => {
  const navigate = useNavigate();
  const [subs, setSubs] = useState(initialSubscriptions);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  // Calculate stats
  const totalMonthly = subs.reduce((total, sub) => {
    if (sub.billingCycle === 'weekly') return total + sub.price * 52 / 12;
    if (sub.billingCycle === 'monthly') return total + sub.price;
    if (sub.billingCycle === 'yearly') return total + sub.price / 12;
    if (sub.billingCycle === 'quarterly') return total + sub.price / 3;
    return total;
  }, 0);
  const totalYearly = totalMonthly * 12;
  // Find upcoming renewals (next 14 days)
  const today = new Date();
  const twoWeeksLater = new Date();
  twoWeeksLater.setDate(today.getDate() + 14);
  const upcomingRenewals = subs.filter(sub => {
    const renewalDate = new Date(sub.nextBillingDate);
    return renewalDate >= today && renewalDate <= twoWeeksLater;
  }).sort((a, b) => {
    return new Date(a.nextBillingDate).getTime() - new Date(b.nextBillingDate).getTime();
  });
  // Get all renewal dates for the calendar
  const renewalDates = subs.map(sub => new Date(sub.nextBillingDate));
  // Calculate spending by category for pie chart
  const spendingByCategory = subs.reduce((acc, sub) => {
    const {
      category,
      price,
      billingCycle
    } = sub;
    let normalizedPrice = price;
    if (billingCycle === 'yearly') normalizedPrice = price / 12;
    if (billingCycle === 'quarterly') normalizedPrice = price / 3;
    if (billingCycle === 'weekly') normalizedPrice = price * 52 / 12;
    if (!acc[category]) {
      acc[category] = {
        name: category,
        value: 0
      };
    }
    acc[category].value += normalizedPrice;
    return acc;
  }, {});
  const categoryData = Object.values(spendingByCategory);
  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
  // Format date
  const formatDate = dateString => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  const getDaysUntil = dateString => {
    const today = new Date();
    const targetDate = new Date(dateString);
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  // Handle subscription click
  const handleSubscriptionClick = id => {
    navigate(`/subscription/${id}`);
  };
  // Handle add subscription
  const handleAddSubscription = (subscription: Omit<Subscription, 'id'> & {
    id?: string;
  }) => {
    // Generate a new ID
    const newId = (Math.max(...subs.map(s => parseInt(s.id))) + 1).toString();
    // Add the new subscription
    setSubs(prev => [...prev, {
      ...subscription,
      id: newId
    } as Subscription]);
    // Close the form
    setShowAddForm(false);
  };
  // Handle date selection in calendar
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    // Find subscriptions due on this date
    const subscriptionsDueOnDate = subs.filter(sub => {
      const renewalDate = new Date(sub.nextBillingDate);
      return renewalDate.getDate() === date.getDate() && renewalDate.getMonth() === date.getMonth() && renewalDate.getFullYear() === date.getFullYear();
    });
    // If there are subscriptions due on this date, you could show them or navigate to them
    if (subscriptionsDueOnDate.length > 0) {
      // For example, you could navigate to the first subscription
      // navigate(`/subscription/${subscriptionsDueOnDate[0].id}`)
      // Or show a notification
      console.log(`${subscriptionsDueOnDate.length} subscriptions due on ${date.toDateString()}`);
    }
  };
  return <div className="space-y-8" data-id="element-335">
      {/* Header with summary stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-336">
        <div className="p-6 pb-0" data-id="element-337">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-338">
            Welcome back
          </h1>
          <p className="text-gray-500" data-id="element-339">
            Here's an overview of your subscription spending
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6" data-id="element-340">
          <div className="flex items-start space-x-4" data-id="element-341">
            <div className="rounded-lg bg-indigo-50 p-3 text-indigo-600" data-id="element-342">
              <CreditCardIcon size={24} data-id="element-343" />
            </div>
            <div data-id="element-344">
              <p className="text-sm font-medium text-gray-500" data-id="element-345">
                Monthly Spending
              </p>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-346">
                ${totalMonthly.toFixed(2)}
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-347">
                Across {subs.length} subscriptions
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-4" data-id="element-348">
            <div className="rounded-lg bg-emerald-50 p-3 text-emerald-600" data-id="element-349">
              <DollarSignIcon size={24} data-id="element-350" />
            </div>
            <div data-id="element-351">
              <p className="text-sm font-medium text-gray-500" data-id="element-352">
                Yearly Spending
              </p>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-353">
                ${totalYearly.toFixed(2)}
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-354">
                Projected annual cost
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-4" data-id="element-355">
            <div className="rounded-lg bg-amber-50 p-3 text-amber-600" data-id="element-356">
              <BellIcon size={24} data-id="element-357" />
            </div>
            <div data-id="element-358">
              <p className="text-sm font-medium text-gray-500" data-id="element-359">
                Upcoming Renewals
              </p>
              <h3 className="text-2xl font-bold text-gray-900" data-id="element-360">
                {upcomingRenewals.length}
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-361">In the next 14 days</p>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-362">
        {/* Expiring Soon Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden lg:col-span-2" data-id="element-363">
          <div className="p-6 pb-3 flex justify-between items-center" data-id="element-364">
            <div data-id="element-365">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-366">Expiring Soon</h2>
              <p className="text-sm text-gray-500" data-id="element-367">
                Subscriptions renewing in the next 14 days
              </p>
            </div>
            <div className="flex items-center space-x-3" data-id="element-368">
              <button onClick={() => setShowAddForm(true)} className="flex items-center px-4 py-2 bg-lime-600 text-white rounded-lg hover:bg-lime-700 text-sm font-medium transition-colors" data-id="element-369">
                <PlusIcon size={16} className="mr-2" data-id="element-370" />
                Add Subscription
              </button>
              <Link to="/subscriptions" className="text-teal-600 hover:text-teal-800 text-sm font-medium flex items-center" data-id="element-371">
                View all <ArrowUpRightIcon size={14} className="ml-1" data-id="element-372" />
              </Link>
            </div>
          </div>
          <div className="px-6 pb-6" data-id="element-373">
            {upcomingRenewals.length > 0 ? <div className="divide-y divide-gray-100" data-id="element-374">
                {upcomingRenewals.map(subscription => {
              const daysLeft = getDaysUntil(subscription.nextBillingDate);
              return <div key={subscription.id} className="py-4 flex items-center justify-between hover:bg-gray-50 rounded-lg px-3 -mx-3 cursor-pointer transition-colors" onClick={() => handleSubscriptionClick(subscription.id)} data-id="element-375">
                      <div className="flex items-center space-x-4" data-id="element-376">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden" data-id="element-377">
                          <img src={subscription.logo} alt={subscription.name} className="w-10 h-10 object-contain" onError={e => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${subscription.name}&background=random&color=fff&size=128`;
                    }} data-id="element-378" />
                        </div>
                        <div data-id="element-379">
                          <h3 className="font-medium text-gray-900" data-id="element-380">
                            {subscription.name}
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-381">
                            {subscription.category}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end" data-id="element-382">
                        <div className="font-semibold text-gray-900" data-id="element-383">
                          ${subscription.price.toFixed(2)}
                          <span className="text-sm text-gray-500 font-normal ml-1" data-id="element-384">
                            /{subscription.billingCycle.slice(0, 2)}
                          </span>
                        </div>
                        <div className="flex items-center mt-1" data-id="element-385">
                          <div className={`h-2 w-2 rounded-full mr-2 ${daysLeft <= 3 ? 'bg-red-500' : daysLeft <= 7 ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-386"></div>
                          <span className="text-sm text-gray-500" data-id="element-387">
                            {daysLeft === 0 ? 'Today' : daysLeft === 1 ? 'Tomorrow' : `${daysLeft} days`}
                            {' - '}
                            {formatDate(subscription.nextBillingDate)}
                          </span>
                        </div>
                      </div>
                    </div>;
            })}
              </div> : <div className="py-8 text-center" data-id="element-388">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 text-gray-400 mb-4" data-id="element-389">
                  <CalendarIcon size={24} data-id="element-390" />
                </div>
                <p className="text-gray-600 font-medium" data-id="element-391">
                  No upcoming renewals
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-392">
                  You're all set for the next 14 days
                </p>
                <button onClick={() => setShowAddForm(true)} className="mt-4 flex items-center px-4 py-2 bg-lime-600 text-white rounded-lg hover:bg-lime-700 text-sm font-medium transition-colors mx-auto" data-id="element-393">
                  <PlusIcon size={16} className="mr-2" data-id="element-394" />
                  Add Subscription
                </button>
              </div>}
          </div>
        </div>
        {/* Calendar Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-395">
          <div className="p-6 pb-3" data-id="element-396">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-397">
              Payment Calendar
            </h2>
            <p className="text-sm text-gray-500" data-id="element-398">
              Your upcoming subscription renewals
            </p>
          </div>
          <div className="px-6 pb-6" data-id="element-399">
            <Calendar value={selectedDate} onChange={handleDateSelect} highlightedDates={renewalDates} data-id="element-400" />
            <div className="mt-4 flex items-center justify-between text-sm" data-id="element-401">
              <div className="flex items-center" data-id="element-402">
                <div className="h-3 w-3 bg-indigo-100 rounded-full mr-2" data-id="element-403"></div>
                <span className="text-gray-600" data-id="element-404">Renewal date</span>
              </div>
              <div className="flex items-center" data-id="element-405">
                <div className="h-3 w-3 border border-blue-500 rounded-full mr-2" data-id="element-406"></div>
                <span className="text-gray-600" data-id="element-407">Today</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Subscription Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-408">
        {/* Category Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden lg:col-span-2" data-id="element-409">
          <div className="p-6 pb-3" data-id="element-410">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-411">
              Category Breakdown
            </h2>
            <p className="text-sm text-gray-500" data-id="element-412">
              Your monthly spending by category
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 px-6 pb-6" data-id="element-413">
            <div className="h-64" data-id="element-414">
              <ResponsiveContainer width="100%" height="100%" data-id="element-415">
                <PieChart data-id="element-416">
                  <Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-417">
                    {categoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-418" />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-col justify-center space-y-3" data-id="element-419">
              {categoryData.map((category, index) => <div key={category.name} className="flex items-center justify-between" data-id="element-420">
                  <div className="flex items-center" data-id="element-421">
                    <div className="w-3 h-3 rounded-sm mr-2" style={{
                  backgroundColor: COLORS[index % COLORS.length]
                }} data-id="element-422"></div>
                    <span className="text-sm font-medium" data-id="element-423">{category.name}</span>
                  </div>
                  <div className="text-sm font-semibold" data-id="element-424">
                    ${category.value.toFixed(2)}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
        {/* Quick Insights */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-425">
          <div className="p-6 pb-3" data-id="element-426">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-427">Quick Insights</h2>
            <p className="text-sm text-gray-500" data-id="element-428">At a glance</p>
          </div>
          <div className="px-6 pb-6 space-y-4" data-id="element-429">
            <Link to="/insights" className="block p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg hover:from-indigo-100 hover:to-blue-100 transition-all" data-id="element-430">
              <div className="flex items-center justify-between mb-2" data-id="element-431">
                <div className="flex items-center text-indigo-600" data-id="element-432">
                  <TrendingUpIcon size={18} className="mr-2" data-id="element-433" />
                  <h3 className="font-medium" data-id="element-434">Spending Trends</h3>
                </div>
                <ArrowUpRightIcon size={14} className="text-indigo-600" data-id="element-435" />
              </div>
              <p className="text-sm text-gray-600" data-id="element-436">
                Your spending is 8% lower than last month
              </p>
            </Link>
            <Link to="/insights" className="block p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg hover:from-emerald-100 hover:to-teal-100 transition-all" data-id="element-437">
              <div className="flex items-center justify-between mb-2" data-id="element-438">
                <div className="flex items-center text-emerald-600" data-id="element-439">
                  <BarChart3Icon size={18} className="mr-2" data-id="element-440" />
                  <h3 className="font-medium" data-id="element-441">Usage Analysis</h3>
                </div>
                <ArrowUpRightIcon size={14} className="text-emerald-600" data-id="element-442" />
              </div>
              <p className="text-sm text-gray-600" data-id="element-443">
                You could save $24.97 by optimizing plans
              </p>
            </Link>
            <Link to="/insights" className="block p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg hover:from-amber-100 hover:to-orange-100 transition-all" data-id="element-444">
              <div className="flex items-center justify-between mb-2" data-id="element-445">
                <div className="flex items-center text-amber-600" data-id="element-446">
                  <PieChartIcon size={18} className="mr-2" data-id="element-447" />
                  <h3 className="font-medium" data-id="element-448">Subscription Mix</h3>
                </div>
                <ArrowUpRightIcon size={14} className="text-amber-600" data-id="element-449" />
              </div>
              <p className="text-sm text-gray-600" data-id="element-450">
                Entertainment makes up 42% of your spending
              </p>
            </Link>
          </div>
        </div>
      </div>
      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-451">
        <div className="p-6 pb-3 flex justify-between items-center" data-id="element-452">
          <div data-id="element-453">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-454">
              Recent Subscriptions
            </h2>
            <p className="text-sm text-gray-500" data-id="element-455">Your active subscriptions</p>
          </div>
          <Link to="/subscriptions" className="text-teal-600 hover:text-teal-800 text-sm font-medium flex items-center" data-id="element-456">
            View all <ArrowUpRightIcon size={14} className="ml-1" data-id="element-457" />
          </Link>
        </div>
        <div className="px-6 pb-6" data-id="element-458">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-459">
            {subs.slice(0, 6).map(subscription => <div key={subscription.id} className="p-4 border border-gray-100 rounded-lg hover:border-gray-200 hover:shadow-sm cursor-pointer transition-all" onClick={() => handleSubscriptionClick(subscription.id)} data-id="element-460">
                <div className="flex items-center space-x-3 mb-3" data-id="element-461">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden" data-id="element-462">
                    <img src={subscription.logo} alt={subscription.name} className="w-8 h-8 object-contain" onError={e => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${subscription.name}&background=random&color=fff&size=128`;
                }} data-id="element-463" />
                  </div>
                  <div data-id="element-464">
                    <h3 className="font-medium text-gray-900" data-id="element-465">
                      {subscription.name}
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-466">
                      {subscription.category}
                    </p>
                  </div>
                </div>
                <div className="flex justify-between items-end" data-id="element-467">
                  <div className="text-xs text-gray-500" data-id="element-468">
                    Next billing: {formatDate(subscription.nextBillingDate)}
                  </div>
                  <div className="font-semibold text-gray-900" data-id="element-469">
                    ${subscription.price.toFixed(2)}
                    <span className="text-xs text-gray-500 font-normal ml-1" data-id="element-470">
                      /{subscription.billingCycle.slice(0, 2)}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      {/* Add Subscription Form */}
      {showAddForm && <SubscriptionForm onSubmit={handleAddSubscription} onCancel={() => setShowAddForm(false)} data-id="element-471" />}
    </div>;
};
export default Home;